import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, Cloud, Sun,
  CloudRain, Snowflake, Wind, X, ExternalLink, Download
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function WeatherCalendar({ token, location, forecast, isDarkMode, compact = false }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: "", time: "12:00", notes: "" });

  const fetchEvents = useCallback(async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await axios.get(`${API}/calendar/events`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month, year }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Failed to fetch calendar events:", error);
    }
  }, [token, currentDate]);

  useEffect(() => {
    if (token) {
      fetchEvents();
    }
  }, [token, currentDate, fetchEvents]);

  const addEvent = async () => {
    if (!newEvent.title || !selectedDate) {
      toast.error("Please enter an event title");
      return;
    }

    try {
      const eventDate = new Date(selectedDate);
      const [hours, minutes] = newEvent.time.split(":");
      eventDate.setHours(parseInt(hours), parseInt(minutes));

      const response = await axios.post(`${API}/calendar/events`, {
        title: newEvent.title,
        date: eventDate.toISOString(),
        notes: newEvent.notes,
        weather_aware: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEvents([...events, response.data.event]);
      setShowAddEvent(false);
      setNewEvent({ title: "", time: "12:00", notes: "" });
      toast.success("Event added to calendar!");
    } catch (error) {
      toast.error("Failed to add event");
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(events.filter(e => e.id !== eventId));
      toast.success("Event deleted");
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const exportToICS = (event) => {
    const startDate = new Date(event.date);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//TheForecastTracker//Weather Calendar//EN
BEGIN:VEVENT
UID:${event.id}@theforecasttracker.com
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${event.title}
DESCRIPTION:${event.notes || ""}${event.weather ? `\\nWeather: ${event.weather.condition}, ${event.weather.temp}°F` : ""}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${event.title.replace(/\s+/g, "_")}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getWeatherForDate = (date) => {
    if (!forecast?.daily || !date) return null;
    
    const today = new Date();
    const dayDiff = Math.floor((date - today) / (1000 * 60 * 60 * 24));
    
    if (dayDiff >= 0 && dayDiff < forecast.daily.length) {
      return forecast.daily[dayDiff];
    }
    return null;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split("T")[0];
    return events.filter(e => e.date.split("T")[0] === dateStr);
  };

  const getWeatherIcon = (weatherId) => {
    if (!weatherId) return <Sun className="w-3 h-3 text-yellow-400" />;
    if (weatherId >= 200 && weatherId < 300) return <CloudRain className="w-3 h-3 text-yellow-400" />;
    if (weatherId >= 300 && weatherId < 600) return <CloudRain className="w-3 h-3 text-blue-400" />;
    if (weatherId >= 600 && weatherId < 700) return <Snowflake className="w-3 h-3 text-white" />;
    if (weatherId >= 700 && weatherId < 800) return <Wind className="w-3 h-3 text-gray-400" />;
    if (weatherId === 800) return <Sun className="w-3 h-3 text-yellow-400" />;
    return <Cloud className="w-3 h-3 text-gray-400" />;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth();

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="weather-calendar-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-[#00f2ea]" />
          <h3 className="font-bold">Weather Calendar</h3>
        </div>
        <Button
          data-testid="calendar-add-event-button"
          size="sm"
          onClick={() => {
            setSelectedDate(new Date());
            setShowAddEvent(true);
          }}
          className="bg-[#00f2ea] text-black hover:bg-[#00d4d4] rounded-lg"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Event
        </Button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button data-testid="calendar-previous-month-button" variant="ghost" size="sm" onClick={prevMonth}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h4 className="font-semibold">
          {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h4>
        <Button data-testid="calendar-next-month-button" variant="ghost" size="sm" onClick={nextMonth}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="calendar-month-grid">
        {/* Day Headers */}
        <div className="grid grid-cols-7 text-center">
          {DAYS.map(day => (
            <div key={day} className={`py-2 text-xs font-medium opacity-60 ${
              isDarkMode ? 'bg-white/5' : 'bg-black/5'
            }`}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((date, idx) => {
            const weather = getWeatherForDate(date);
            const dayEvents = getEventsForDate(date);
            const today = isToday(date);

            return (
              <div
                key={`item-${idx}`}
                onClick={() => date && (setSelectedDate(date), setShowAddEvent(true))}
                data-testid={date ? `calendar-day-${date.getDate()}` : `calendar-empty-day-${idx}`}
                className={`${compact ? 'min-h-[42px]' : 'min-h-[70px]'} p-1 border-t ${
                  isDarkMode ? 'border-white/5' : 'border-black/5'
                } ${date ? 'cursor-pointer hover:bg-white/5' : ''} ${
                  today ? (isDarkMode ? 'bg-[#00f2ea]/10' : 'bg-cyan-50') : ''
                }`}
              >
                {date && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${
                        today ? 'text-[#00f2ea]' : ''
                      }`}>
                        {date.getDate()}
                      </span>
                      {weather && (
                        <div className="flex items-center gap-1">
                          {getWeatherIcon(weather.weather?.[0]?.id)}
                          <span className="text-[10px]">
                            {Math.round(weather.temp?.max || weather.temp)}°
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Events */}
                    <div className="mt-1 space-y-0.5">
                      {dayEvents.slice(0, 2).map(event => (
                        <div
                          key={event.id}
                          className="text-[10px] px-1 py-0.5 rounded bg-[#00f2ea]/20 text-[#00f2ea] truncate"
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-[10px] opacity-50">
                          +{dayEvents.length - 2} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Events */}
      {!compact && events.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2 opacity-70">Upcoming Events</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {events
              .filter(e => new Date(e.date) >= new Date())
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .slice(0, 5)
              .map(event => (
                <div
                  key={event.id}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    isDarkMode ? 'bg-white/5' : 'bg-black/5'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{event.title}</p>
                    <p className="text-xs opacity-50">
                      {new Date(event.date).toLocaleDateString()} at {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`calendar-export-event-${event.id}-button`}
                      variant="ghost"
                      size="sm"
                      onClick={() => exportToICS(event)}
                      className="p-1"
                      title="Export to calendar"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`calendar-delete-event-${event.id}-button`}
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteEvent(event.id)}
                      className="p-1 text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      <AnimatePresence>
        {showAddEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowAddEvent(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md rounded-2xl p-6 ${
                isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-bold text-lg mb-4">Add Weather Event</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm opacity-60 mb-1 block">Date</label>
                  <Input
                    type="date"
                    value={selectedDate?.toISOString().split("T")[0] || ""}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                    className={isDarkMode ? 'bg-white/5' : 'bg-black/5'}
                  />
                </div>
                
                <div>
                  <label className="text-sm opacity-60 mb-1 block">Time</label>
                  <Input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    className={isDarkMode ? 'bg-white/5' : 'bg-black/5'}
                  />
                </div>
                
                <div>
                  <label className="text-sm opacity-60 mb-1 block">Event Title</label>
                  <Input
                    placeholder="e.g., Outdoor BBQ, Hiking Trip..."
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    className={isDarkMode ? 'bg-white/5' : 'bg-black/5'}
                  />
                </div>
                
                <div>
                  <label className="text-sm opacity-60 mb-1 block">Notes (optional)</label>
                  <Input
                    placeholder="Any additional notes..."
                    value={newEvent.notes}
                    onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                    className={isDarkMode ? 'bg-white/5' : 'bg-black/5'}
                  />
                </div>

                {/* Weather Preview */}
                {selectedDate && getWeatherForDate(selectedDate) && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                    <p className="text-xs opacity-60 mb-1">Weather Forecast</p>
                    <div className="flex items-center gap-2">
                      {getWeatherIcon(getWeatherForDate(selectedDate)?.weather?.[0]?.id)}
                      <span className="text-sm">
                        {getWeatherForDate(selectedDate)?.weather?.[0]?.description || "Clear"} •{" "}
                        {Math.round(getWeatherForDate(selectedDate)?.temp?.max || getWeatherForDate(selectedDate)?.temp)}°F
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setShowAddEvent(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={addEvent}
                    className="bg-[#00f2ea] text-black hover:bg-[#00d4d4]"
                  >
                    Add Event
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
